/**
 * SideNavHierarchical — PM nav rail (W124.3 · 2026-05-26).
 *
 * Reescrito em W124.3 para consumir `getNavForRole(role, projectId)`
 * (PM_NAV + ADMIN_NAV) ao invés da NAV_HIERARCHY antiga (que ainda
 * existe deprecated em `nav-hierarchy.ts` apenas pro MobileBottomNav
 * "Mais" overlay).
 *
 * Estrutura visual (desktop ≥md, expanded):
 *   ┌──────────────────────────┐
 *   │ Brand header (56px)      │
 *   ├──────────────────────────┤
 *   │ PM nav (7 links flat)    │
 *   │  Hoje                    │
 *   │  Plano                   │
 *   │  Compras                 │
 *   │  Obra                    │
 *   │  Dinheiro                │
 *   │  Entrega                 │
 *   │  Configurações           │
 *   │                          │
 *   │  ▼ Admin (admin role)    │
 *   │    · Comando             │
 *   │    · Painel Admin        │
 *   │    · …15 items           │
 *   ├──────────────────────────┤
 *   │ Pinned (Settings, Ajuda) │ ← from nav-registry (deprecated path)
 *   ├──────────────────────────┤
 *   │ Collapse toggle          │
 *   └──────────────────────────┘
 *
 * Mobile (<md): full-height drawer, slides in/out via `open` prop.
 * Owner / broker / client never reach this component (their layout
 * is owned by `PortalShell`).
 *
 * Active-state: `bg-brand-500/10 + text-brand-500`, no border stripe.
 *
 * Storage:
 *   `builder.sidenav.admin-expanded` → boolean (admin section state).
 *   Per-role mode storage of the legacy SideNav is NOT reused.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { UserRole } from '@builder/shared';
import { useAuth } from '@/store/auth.store';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';
import { AnimatedLogo } from '@/components/AnimatedLogo';
import { BuilderMark } from '@/components/BuilderMark';
import {
  getNavForRole,
  getPinnedNavEntriesForRole,
  getBrandHomeForRole,
  type ResolvedNavGroup,
} from '@/lib/nav-registry';

// ─────────────────────────── constants ────────────────────────────────

const ADMIN_EXPANDED_KEY = 'builder.sidenav.admin-expanded';

/**
 * Extract projId from a pathname like `/projects/abc123/rdos`.
 * Returns '' when not inside a project route.
 */
function extractProjId(pathname: string): string {
  const m = pathname.match(/^\/projects\/([^/]+)/);
  return m ? (m[1] ?? '') : '';
}

/** Resolve a dot-separated i18n key from a dict-shaped object. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveKey(key: string, t: any): string {
  const parts = key.split('.');
  let cursor: unknown = t;
  for (const part of parts) {
    if (cursor && typeof cursor === 'object' && part in (cursor as Record<string, unknown>)) {
      cursor = (cursor as Record<string, unknown>)[part];
    } else {
      return key; // defensive fallback
    }
  }
  return typeof cursor === 'string' ? cursor : key;
}

// ─────────────────── component ────────────────────────────────────────

export function SideNavHierarchical({
  open,
  onClose,
  collapsed,
  onToggleCollapsed,
}: {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Reserved for API back-compat with old call sites — no-op. */
  multiExpand?: boolean;
}) {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const user = useAuth((s) => s.user);
  const location = useLocation();

  const projId = extractProjId(location.pathname);
  const role: UserRole | null = user?.role ?? null;

  // Auto-close on route change (mobile)
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      onClose();
    }
  }, [location.pathname, onClose]);

  // PM/ADMIN nav from registry (W124.3 canonical).
  // Owner/client/broker have their own layout (PortalShell) and never
  // reach here — but we still guard defensively so a misrouted render
  // doesn't crash the shell.
  const resolved = useMemo<ResolvedNavGroup[]>(
    () => (role ? getNavForRole(role, projId || undefined) : []),
    [role, projId],
  );
  const pmGroups = useMemo(() => resolved.filter((g) => !g.isAdmin), [resolved]);
  const adminGroups = useMemo(() => resolved.filter((g) => g.isAdmin), [resolved]);

  // Admin section expand state — persisted booleano.
  const [adminExpanded, setAdminExpanded] = useState<boolean>(() => {
    try {
      const raw = window.localStorage.getItem(ADMIN_EXPANDED_KEY);
      return raw === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(ADMIN_EXPANDED_KEY, adminExpanded ? '1' : '0');
    } catch {
      /* localStorage full / disabled */
    }
  }, [adminExpanded]);

  // Auto-expand admin when the current path is inside an admin route.
  useEffect(() => {
    if (!adminGroups.length) return;
    const inAdmin = location.pathname.startsWith('/admin');
    if (inAdmin && !adminExpanded) setAdminExpanded(true);
    // never auto-collapse — user choice persists outside /admin/*
  }, [location.pathname, adminGroups.length, adminExpanded]);

  if (!user || !role) return null;

  // Owner-portal roles should not be using this sidebar (PortalShell
  // owns their layout). If they somehow render it, bail out — never
  // leak PM nav to a client/broker user. `owner` is not in UserRole
  // (the Owner Portal uses `client`/`broker`), so we just guard those.
  if (role === 'client' || role === 'broker') return null;

  const pinnedEntries = getPinnedNavEntriesForRole(role);
  const brandHome = getBrandHomeForRole(role);
  const railWidth = collapsed ? 'w-[68px]' : 'w-[256px]';

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-200 md:hidden',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        aria-hidden
      />

      <aside
        aria-label={isEn ? 'Main navigation' : 'Navegação principal'}
        data-sidenav-w124
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-line bg-surface',
          'transition-[width,transform] duration-200 ease-out',
          railWidth,
          open ? 'translate-x-0' : '-translate-x-full',
          'md:translate-x-0',
        )}
      >
        {/* Brand row */}
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-line px-3">
          {collapsed ? (
            <Link
              to={brandHome}
              aria-label={t.common.appName}
              className="mx-auto flex h-11 w-11 items-center justify-center rounded-md hover:bg-surface-2"
            >
              <BuilderMark className="h-7 w-7 text-ink" />
            </Link>
          ) : (
            <AnimatedLogo variant="bar" href={brandHome} label={t.common.appName} />
          )}
        </div>

        {/* PM nav + Admin section */}
        <div className="flex-1 overflow-y-auto py-2">
          <ul className="space-y-0.5 px-2" data-testid="pm-nav-list">
            {pmGroups.map((g) => (
              <PmGroupRow
                key={g.id}
                group={g}
                pathname={location.pathname}
                collapsed={collapsed}
                t={t}
                isEn={isEn}
              />
            ))}
          </ul>

          {adminGroups.length > 0 && (
            <AdminSection
              groups={adminGroups}
              expanded={adminExpanded}
              onToggle={() => setAdminExpanded((v) => !v)}
              pathname={location.pathname}
              collapsed={collapsed}
              t={t}
            />
          )}
        </div>

        {/* Pinned bottom block (Settings, Ajuda, …) — from legacy registry. */}
        {pinnedEntries.length > 0 && (
          <div className="shrink-0 border-t border-line px-2 py-2">
            <ul className="space-y-0.5">
              {pinnedEntries.map((entry) => {
                const active =
                  location.pathname === entry.to ||
                  location.pathname.startsWith(`${entry.to}/`);
                const label = resolveKey(entry.labelKey, t);
                return (
                  <li key={entry.id}>
                    <Link
                      to={entry.to}
                      aria-label={label}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex min-h-[44px] items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] transition-colors',
                        active
                          ? 'bg-brand-500/10 text-brand-500'
                          : 'text-ink-muted hover:bg-surface-3 hover:text-ink',
                      )}
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                        <PinnedIcon id={entry.id} />
                      </span>
                      {!collapsed && <span className="truncate">{label}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Collapse toggle */}
        <div className="shrink-0 border-t border-line p-2">
          <button
            type="button"
            onClick={onToggleCollapsed}
            className={cn(
              'pressable flex min-h-[44px] w-full items-center gap-2 rounded-md px-2 py-2 text-[12px] font-medium',
              'text-ink-muted hover:bg-surface-2 hover:text-ink',
              collapsed ? 'justify-center' : 'justify-start',
            )}
            aria-label={collapsed
              ? (isEn ? 'Expand menu' : 'Expandir menu')
              : (isEn ? 'Collapse menu' : 'Recolher menu')}
          >
            <ChevronDir dir={collapsed ? 'right' : 'left'} />
            {!collapsed && <span>{isEn ? 'Collapse' : 'Recolher'}</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

// ───────────────────────── PM group row ──────────────────────────────

function PmGroupRow({
  group,
  pathname,
  collapsed,
  t,
  isEn,
}: {
  group: ResolvedNavGroup;
  pathname: string;
  collapsed: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
  isEn: boolean;
}) {
  const label = resolveKey(group.labelKey, t);
  // Active = exact match or any sub-route. For per-project groups, also
  // match the path segment after `/projects/:id/<groupKey>`.
  const to = group.to;
  const active = pathname === to || pathname.startsWith(`${to}/`);
  const disabled = group.requiresProject && to.includes('/projects//');

  if (disabled) {
    // W153 P2 · sem obra selecionada, o item não vira <span> inerte: linka
    // pro picker de obra (`/campo`). Antes o usuário via "Obra" cinza sem
    // caminho; agora um clique leva pra onde se escolhe a obra.
    return (
      <li>
        <Link
          to="/campo"
          aria-label={isEn ? `${label} — select a project first` : `${label} — selecione uma obra primeiro`}
          title={isEn ? 'Select a project first' : 'Selecione uma obra primeiro'}
          data-testid={`pm-nav-${group.id}-disabled`}
          className={cn(
            'flex min-h-[44px] w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px]',
            'text-ink-soft opacity-60 transition-colors hover:bg-surface-3 hover:opacity-100',
          )}
        >
          <span className="flex h-4 w-4 shrink-0 items-center justify-center">
            <PmGroupIcon icon={group.icon} />
          </span>
          {!collapsed && <span className="truncate">{label}</span>}
        </Link>
      </li>
    );
  }

  return (
    <li>
      <Link
        to={to}
        aria-label={label}
        aria-current={active ? 'page' : undefined}
        title={collapsed ? label : undefined}
        data-testid={`pm-nav-${group.id}`}
        className={cn(
          'flex min-h-[44px] items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] transition-colors',
          active
            ? 'bg-brand-500/10 text-brand-500'
            : 'text-ink-muted hover:bg-surface-3 hover:text-ink',
          collapsed && 'justify-center',
        )}
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
          <PmGroupIcon icon={group.icon} />
        </span>
        {!collapsed && <span className="truncate">{label}</span>}
      </Link>
    </li>
  );
}

// ───────────────────────── Admin section ─────────────────────────────

function AdminSection({
  groups,
  expanded,
  onToggle,
  pathname,
  collapsed,
  t,
}: {
  groups: ResolvedNavGroup[];
  expanded: boolean;
  onToggle: () => void;
  pathname: string;
  collapsed: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}) {
  return (
    <div className="mt-2 border-t border-line pt-2 px-2" data-testid="admin-section">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={cn(
          'pressable flex min-h-[44px] w-full items-center gap-2 rounded-md px-2 py-2 text-[12px] font-medium uppercase tracking-wide',
          'text-ink-muted hover:bg-surface-2',
        )}
      >
        {!collapsed && <span className="flex-1 text-left">Admin</span>}
        <span
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center transition-transform duration-[220ms] ease-out',
            expanded ? 'rotate-90' : 'rotate-0',
          )}
        >
          <ChevronDir dir="right" />
        </span>
      </button>

      {!collapsed && (
        <div
          className={cn(
            'overflow-hidden transition-[max-height,opacity] duration-[220ms] ease-out',
            expanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0',
          )}
        >
          <ul className="pb-1 space-y-0.5 pt-1">
            {groups.map((g) => {
              const label = resolveKey(g.labelKey, t);
              const active = pathname === g.to || pathname.startsWith(`${g.to}/`);
              return (
                <li key={g.id}>
                  <Link
                    to={g.to}
                    aria-label={label}
                    aria-current={active ? 'page' : undefined}
                    data-testid={`admin-nav-${g.id}`}
                    className={cn(
                      'flex min-h-[44px] items-center rounded-md pl-7 pr-2.5 py-1.5 text-[13px] transition-colors',
                      active
                        ? 'bg-brand-500/10 text-brand-500'
                        : 'text-ink-muted hover:bg-surface-3 hover:text-ink',
                    )}
                  >
                    <span className="truncate">{label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── icons ─────────────────────────────────────

// Compact icon set used by PM_NAV groups. Map Lucide-ish names → svg paths.
const PM_ICON_PATHS: Record<string, string | string[]> = {
  Home: 'M3 11.5L12 4l9 7.5V20a1 1 0 0 1-1 1h-4.5v-6.5h-7V21H4a1 1 0 0 1-1-1v-8.5z',
  FileText: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6 M8 13h8 M8 17h6'],
  ShoppingCart: ['M9 22a1 1 0 1 0 0-2 1 1 0 0 0 0 2z', 'M20 22a1 1 0 1 0 0-2 1 1 0 0 0 0 2z', 'M1 1h4l2.7 13.5a2 2 0 0 0 2 1.5h9.4a2 2 0 0 0 2-1.6L23 6H6'],
  HardHat: 'M4 13a8 8 0 0 1 16 0v3H4z M2 16h20 M9 13V8a3 3 0 0 1 6 0v5',
  Wallet: ['M21 12V7H5a2 2 0 0 1 0-4h14v4', 'M3 5v14a2 2 0 0 0 2 2h16v-5', 'M18 12a2 2 0 1 0 0 4h4v-4z'],
  Package: ['M16.5 9.4L7.5 4.21', 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z', 'M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12'],
  Settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.8 1.2V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-2.8-1.2l-.1.1a2 2 0 0 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.6 15H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.2-2.8l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9.9 6H12',
};

function PmGroupIcon({ icon }: { icon: string }) {
  const raw = PM_ICON_PATHS[icon] ?? 'M12 12m-3 0a3 3 0 1 0 6 0 3 3 0 1 0-6 0';
  const paths = Array.isArray(raw) ? raw : [raw];
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}

function ChevronDir({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={dir === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
    </svg>
  );
}

// Minimal pinned-entry icon set (Settings/Ajuda fallback).
function PinnedIcon({ id }: { id: string }) {
  const paths: Record<string, string> = {
    settings:
      'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.8 1.2V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-2.8-1.2l-.1.1a2 2 0 0 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.6 15H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.2-2.8l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9.9 6H12',
    ajuda:
      'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M9.5 9a2.5 2.5 0 0 1 5 0c0 2-2.5 2-2.5 4M12 17h.01',
    'admin-usuarios-list':
      'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
    owner:
      'M3 11.5L12 4l9 7.5V20a1 1 0 0 1-1 1h-4.5v-6.5h-7V21H4a1 1 0 0 1-1-1v-8.5z',
  };
  const d = paths[id] ?? 'M12 12m-3 0a3 3 0 1 0 6 0 3 3 0 1 0-6 0';
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
    </svg>
  );
}
